import { HttpRequest, Upload } from "tus-js-client";
import BaseUpload, { InitialFile } from "./base_upload";
import { deleteUpload } from "./tus_utils";

interface Parameters {
    chunkSize: number;
    csrfToken: string;
    file: File;
    retryDelays: number[] | null;
    uploadIndex: number;
    uploadUrl: string;
}

export default class TusUpload extends BaseUpload {
    public onError?: (error: Error) => void;
    public onProgress?: (bytesUploaded: number, bytesTotal: number) => void;
    public onSuccess?: () => void;
    private upload: Upload;
    private csrfToken: string;

    constructor({
        chunkSize,
        csrfToken,
        file,
        retryDelays,
        uploadIndex,
        uploadUrl
    }: Parameters) {
        super({ name: file.name, status: "uploading", type: "tus", uploadIndex });

        this.csrfToken = csrfToken;

        this.upload = new Upload(file, {
            chunkSize,
            endpoint: uploadUrl,
            metadata: {
                filename: file.name,
            },
            onBeforeRequest: this.addCsrTokenToRequest,
            onError: this.handleError,
            onProgress: this.handleProgress,
            onSuccess: this.handleSucces,
            retryDelays: retryDelays || [0, 1000, 3000, 5000]
        });

        this.onError = undefined;
        this.onProgress = undefined;
        this.onSuccess = undefined;
    }

    public async abort(): Promise<void> {
        await this.upload.abort(true);
    }

    public async delete(): Promise<void> {
        if (!this.upload.url) {
            return Promise.resolve();
        }

        await deleteUpload(this.upload.url, this.csrfToken);
    }

    public getSize(): number {
        return (this.upload.file as File).size;
    }

    public getUrl(): string | null {
        return this.upload.url
    }

    public start(): void {
        this.upload.start();
    }

    private handleError = (error: Error) => {
        if (this.onError) {
            this.onError(error);
        } else {
            throw error;
        }
    };

    private handleProgress = (bytesUploaded: number, bytesTotal: number) => {
        if (this.onProgress) {
            this.onProgress(bytesUploaded, bytesTotal);
        }
    };

    private handleSucces = () => {
        if (this.onSuccess) {
            this.onSuccess();
        }
    };

    private addCsrTokenToRequest = (request: HttpRequest) => {
        request.setHeader("X-CSRFToken", this.csrfToken);
    };

    getInitialFile(): InitialFile {
        return {
            id: this.name,
            name: this.name,
            size: this.getSize(),
            type: "tus",
            url: ""
        };
    }
}
